<template>
    <div class="h-full min-w-0 overflow-hidden">
        <div class="bg-gray-200 dark:bg-gray-700 rounded-lg overflow-hidden max-h-[calc(100%-20px)] overflow-y-auto min-w-0">
            <!-- Time column header -->
            <table class="w-full bg-white dark:bg-gray-800 p-3 text-center min-w-0 table-fixed">
                <thead>
                    <tr>
                        <td class="bg-white dark:bg-gray-800 p-3 text-center text-xs text-gray-500 dark:text-gray-400 p-1 text-center border-r border-b border-gray-100 dark:border-gray-700 w-16">
                            Time
                        </td>
                        <td v-for="date in weekDates" :key="date.toISOString()" 
                            class="bg-white dark:bg-gray-800 p-3 text-center text-xs text-gray-500 dark:text-gray-400 p-1 text-center border-r border-b border-gray-100 dark:border-gray-700 min-w-0 overflow-hidden">
                            {{ getDayName(date) }} {{ date.getDate() }}
                        </td>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="hour in 24" :key="hour">
                        <td class="bg-white dark:bg-gray-800 p-3 text-center border-r border-b border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 p-1 text-center w-16">
                            {{ formatHour(hour - 1) }}
                        </td>
                        <td v-for="date in weekDates" :key="date.toISOString()" 
                            class="bg-white dark:bg-gray-800 p-3 text-center border-r border-b border-gray-100 dark:border-gray-700 min-w-0 overflow-hidden"
                            @click="addEventAtTime(date, hour - 1)"
                            :class="{
                                'bg-indigo-50 dark:bg-indigo-900/20': isToday(date)
                            }"
                        >
                            <!-- Events in this time slot -->
                            <div 
                                v-for="event in getEventsForDateAndTime(date, hour - 1).slice(0, 3)" 
                                :key="event.id"
                                @click.stop="selectEvent(event)"
                                class="text-xs p-1 rounded cursor-pointer truncate"
                                :class="getEventClasses(event)"
                                :style="getEventStyles(event)"
                                :title="event.title"
                            >
                                {{ event.title }}
                            </div>
                            <!-- See more button when there are more than 3 events in this time slot -->
                            <button 
                                v-if="getEventsForDateAndTime(date, hour - 1).length > 3"
                                @click.stop="seeMoreEvents(date, hour - 1)"
                                class="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium w-full text-left p-1 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors border border-indigo-200 dark:border-indigo-700 hover:border-indigo-300 dark:hover:border-indigo-600"
                            >
                                +{{ getEventsForDateAndTime(date, hour - 1).length - 3 }} more
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import { isTailwindColor } from '@/utilities/color'
import { useI18n } from 'vue-i18n'

// Props
const props = defineProps({
    currentDate: {
        type: Date,
        default: () => new Date()
    },
    events: {
        type: Array,
        default: () => []
    },
    categories: {
        type: Array,
        default: () => []
    }
})

// Emits
const emit = defineEmits(['event-select', 'add-event', 'see-more'])

const { locale } = useI18n()

// Computed properties
const weekDates = computed(() => {
    const start = getWeekStart(props.currentDate)
    const dates = []
    for (let i = 0; i < 7; i++) {
        const date = new Date(start)
        date.setDate(start.getDate() + i)
        dates.push(date)
    }
    return dates
})

// Methods
const isToday = (date) => {
    const today = new Date()
    return date.toDateString() === today.toDateString()
}

const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date.getTime() < today.getTime();
}

const getEventsForDateAndTime = (date, hour) => {
    const slotStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, 0, 0, 0)
    const slotEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, 59, 59, 999)
    return props.events.filter(event => {
        const start = new Date(event.startTime || event.date)
        const end = event.endTime ? new Date(event.endTime) : start
        return start.getTime() <= slotEnd.getTime() && end.getTime() > slotStart.getTime()
    })
}

const getEventStyles = (event) => {
    const category = props.categories.find(c => c.name === event.category)
    const color = category ? category.color : 'gray'
    
    if (isTailwindColor(color)) {
        return {}
    }
    return {
        backgroundColor: color
    }
}

const getEventClasses = (event) => {
    const category = props.categories.find(c => c.name === event.category)
    const color = category ? category.color : 'gray'
    const baseClasses = 'text-white text-xs font-medium'
    
    if (isTailwindColor(color)) {
        return `${baseClasses} bg-${color}-500 hover:bg-${color}-600`
    }
    return baseClasses
}

const getWeekStart = (date) => {
    const d = new Date(date)
    const day = d.getDay()
    const diff = d.getDate() - day + (day === 0 ? -6 : 1)
    return new Date(d.setDate(diff))
}

const getDayName = (date) => {
    return date.toLocaleDateString(locale.value, { weekday: 'short' })
}

const formatHour = (hour) => {
    return hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`
}

const selectEvent = (event) => {
    emit('event-select', event)
}

const addEventAtTime = (date, hour) => {
    if (isPastDate(date)) return;
    const time = new Date(date)
    time.setHours(hour, 0, 0, 0)
    emit('add-event', { date, time })
}

const seeMoreEvents = (date, hour) => {
    const events = getEventsForDateAndTime(date, hour)
    emit('see-more', { date, events, hour })
}
</script>
